import React, { useEffect, useState, forwardRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Collapse,
} from '@mui/material';
import {
  IconApps,
  IconPlus,
  IconAlertTriangle,
  IconAlertCircle,
  IconChevronRight,
} from '@tabler/icons-react';
import { CollapsibleSection } from './CollapsibleSection';
import { SECONDARY_BTN_SX, type PanelDensity } from './panelStyles';
import { CustomerServiceInlineEditor } from './CustomerServiceDrawer';
import { AddCustomerServiceModal } from './AddCustomerServiceModal';
import type { CustomerServiceStatus, CustomerServiceCreateRequest } from '@techgitter/contracts';
import type { CustomerServiceUpdateRequest } from '@techgitter/contracts';

/** Service catalog key for ESET — its row launches the ESET management panel
 *  instead of the generic inline status editor. */
const ESET_SERVICE_KEY = 'ESET';

/** The API sends CSS colour names for the status dot (see STATUS_METADATA in
 *  sm4rt-api). Bare `green` is too dark against the light row — map it to the
 *  palette's softer green. Anything not listed passes through untouched. */
const STATUS_DOT_COLOURS: Record<string, string> = {
  green: '#6dcb6d',
};

interface CustomerServicesPanelProps {
  dataverseCustomerId: string | null;
  services: CustomerServiceStatus[];
  isLoading: boolean;
  error: string | null;
  canEditServices: boolean;
  refresh: () => void;
  create: (payload: CustomerServiceCreateRequest) => Promise<void>;
  update: (customerServiceId: string, updates: CustomerServiceUpdateRequest) => Promise<void>;
  onServiceSelect?: (serviceId: string) => void;
  focusServiceId?: string | null;
  focusRequestKey?: number;
  /** Density tier — `comfortable` (customer-detail card) or `compact`
   *  (default; New Ticket sidebar). See panelStyles. */
  density?: PanelDensity;
  /** When set, the ESET service row launches the ESET management panel (a right
   *  slide-over) instead of the generic inline status editor. Hosts that don't
   *  render the panel omit this — the ESET row is then inert. */
  onManageEset?: () => void;
  /** Customer display name, shown in the Gorelo add confirmation. */
  customerName?: string | null;
}

export const CustomerServicesPanel = forwardRef<HTMLDivElement, CustomerServicesPanelProps>(({
  dataverseCustomerId,
  services,
  isLoading,
  error,
  canEditServices,
  refresh,
  create,
  update,
  onServiceSelect,
  focusServiceId = null,
  focusRequestKey = 0,
  density = 'compact',
  onManageEset,
  customerName = null,
}, ref) => {
  const isComfortable = density === 'comfortable';
  const [expanded, setExpanded] = useState(false);
  const [expandedServiceId, setExpandedServiceId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (!focusServiceId) return;
    setExpanded(true);
    setExpandedServiceId(focusServiceId);
  }, [focusServiceId, focusRequestKey]);

  const handleRowClick = (service: CustomerServiceStatus) => {
    // ESET is managed in a dedicated right panel, never the generic status editor.
    if (service.serviceKey === ESET_SERVICE_KEY) {
      if (onManageEset) onManageEset();
      return;
    }
    setExpandedServiceId(prev => (prev === service.customerServiceId ? null : service.customerServiceId));
    if (onServiceSelect) {
      onServiceSelect(service.customerServiceId);
    }
  };

  const handleDrawerClose = () => {
    setExpandedServiceId(null);
  };

  const handleDrawerSave = async (customerServiceId: string, updates: CustomerServiceUpdateRequest) => {
    if (!dataverseCustomerId) return;
    await update(customerServiceId, updates);
  };

  const handleAddService = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleModalSubmit = async (payload: CustomerServiceCreateRequest) => {
    await create(payload);
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <IconAlertCircle size={15} stroke={1.5} style={{ color: '#B03A2E', flexShrink: 0 }} />;
      case 'warning':
        return <IconAlertTriangle size={15} stroke={1.5} style={{ color: '#C07A10', flexShrink: 0 }} />;
      default:
        return null;
    }
  };

  const canAddServices = Boolean(dataverseCustomerId) && canEditServices;

  return (
    <>
      <CollapsibleSection
        ref={ref}
        icon={IconApps}
        title="Services"
        count={services.length}
        onRefresh={refresh}
        expanded={expanded}
        onToggle={() => setExpanded(!expanded)}
        bodyPadding={0}
      >
          <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              startIcon={<IconPlus size={16} stroke={1.5} />}
              onClick={handleAddService}
              variant="outlined"
              disabled={!canAddServices}
              sx={SECONDARY_BTN_SX}
            >
              Add service
            </Button>
          </Box>
          {error && (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
              <Button size="small" onClick={refresh} sx={{ ml: 1 }}>
                Retry
              </Button>
            </Alert>
          )}
          {!isLoading && services.length === 0 && !error && (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No services yet.
              </Typography>
              {!dataverseCustomerId && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Select a customer to add services.
                </Typography>
              )}
            </Box>
          )}
          <Box>
            {services.map((service, index) => {
              const status = service.status ?? {
                value: 0,
                label: 'Status unavailable',
                colour: 'action.disabled',
                severity: 'info' as const,
              };

              return (
                <React.Fragment key={service.customerServiceId}>
                  <Box
                    onClick={() => handleRowClick(service)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      px: 2,
                      py: isComfortable ? 1.25 : 1,
                      borderTop: index > 0 ? '0.5px solid #e2eaf3' : 'none',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: '#f7f9fc' },
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: STATUS_DOT_COLOURS[status.colour] ?? status.colour,
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                      <Typography
                        noWrap
                        sx={{ fontSize: isComfortable ? 14 : 13, fontWeight: 500, color: '#1a1a1a' }}
                      >
                        {service.displayName}
                      </Typography>
                      {status.severity !== 'info' && getSeverityIcon(status.severity)}
                      <Typography
                        noWrap
                        sx={{ fontSize: 12, fontStyle: 'italic', color: '#646464', flexShrink: 0 }}
                      >
                        {service.startDate
                          ? `${status.label} since ${new Date(service.startDate).toLocaleDateString('en-AU', { month: 'short', year: '2-digit' })}`
                          : status.label}
                      </Typography>
                    </Box>
                    {service.serviceKey === ESET_SERVICE_KEY && onManageEset && (
                      <IconChevronRight size={16} stroke={1.5} style={{ color: '#bbb', flexShrink: 0 }} />
                    )}
                  </Box>
                  <Collapse in={expandedServiceId === service.customerServiceId} timeout="auto" unmountOnExit>
                    <CustomerServiceInlineEditor
                      service={service}
                      canEdit={canEditServices}
                      onClose={handleDrawerClose}
                      onSave={handleDrawerSave}
                    />
                  </Collapse>
                </React.Fragment>
              );
            })}
          </Box>
      </CollapsibleSection>

      <AddCustomerServiceModal
        open={modalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        existingServiceIds={services.map(s => s.serviceId)}
        customerName={customerName}
      />
    </>
  );
});
